/**
 * Read-only loader for printable sales-order documents.
 * SELECT only. Never mutate orders, inventory, shipments, or providers.
 */

import { supabase } from "@/lib/supabase";
import { firstRelation } from "@/lib/relations";
import { hasDetachedCatalogueIdentity, historicalProductName, historicalVariantId } from "@/lib/catalogue-history";
import { parseOrgBusinessIdentity } from "@/lib/org-business-identity";
import { signMediaUrls } from "@/lib/media";
import {
  assertTenantIsolation,
  buildOrderDocumentModel,
  chooseCanonicalLineImage,
  openPrintWindow,
  printableMediaSrc,
  renderOrderDocumentsHtml,
  shippingLabelPrintState,
  type OrderDocumentKind,
  type OrderDocumentModel,
} from "@/lib/order-documents";
import type { AllocationLedgerEntry } from "@/lib/warehouse-projection";

export const ORDER_DOCUMENT_LOADER_MUTATES = false;

async function resolvePrintIdentity(identity: ReturnType<typeof parseOrgBusinessIdentity>) {
  const raw = (identity.logoUrl ?? "").trim();
  if (!raw) return identity;
  if (/^https?:\/\//i.test(raw)) return identity;
  try {
    const signed = await signMediaUrls([raw]);
    const src = printableMediaSrc(raw, signed);
    return { ...identity, logoUrl: src };
  } catch {
    return { ...identity, logoUrl: null };
  }
}

export async function loadOrderDocumentModels(orgId: string, orderIds: string[]): Promise<OrderDocumentModel[]> {
  const ids = [...new Set(orderIds.filter(Boolean))];
  if (!orgId || ids.length === 0) return [];

  const [orgRes, ordersRes] = await Promise.all([
    supabase.from("organizations").select("id, name, settings").eq("id", orgId).maybeSingle(),
    supabase
      .from("sales_orders")
      .select(
        "id, org_id, order_number, order_date, source, channel_id, location_id, customer_id, payment_status, customer_reference, notes, subtotal, discount, tax, shipping, total, currency, billing_address, shipping_address, customers(name, phone), locations(name), connectors(name, provider)",
      )
      .eq("org_id", orgId)
      .in("id", ids),
  ]);
  if (orgRes.error) throw new Error(orgRes.error.message);
  if (ordersRes.error) throw new Error(ordersRes.error.message);

  const orders = (ordersRes.data ?? []).map((row) => ({
    ...row,
    customers: firstRelation(row.customers),
    locations: firstRelation(row.locations),
    connectors: firstRelation(row.connectors),
  }));
  const foundIds = orders.map((o) => o.id as string);
  if (foundIds.length === 0) return [];

  const [itemsRes, externalRes, fulfilRes] = await Promise.all([
    supabase
      .from("sales_order_items")
      .select(
        "id, sales_order_id, product_id, variant_id, catalogue_product_identity_id, catalogue_variant_identity_id, qty_ordered, qty_allocated, qty_fulfilled, unit_price, total, is_bundle, product_name, sku, variant_attributes, products(name, sku, barcode), variants(sku, barcode, option_values)",
      )
      .eq("org_id", orgId)
      .in("sales_order_id", foundIds)
      .order("created_at"),
    supabase
      .from("external_identifiers")
      .select("entity_id, external_id")
      .eq("org_id", orgId)
      .eq("entity_type", "sales_order")
      .in("entity_id", foundIds),
    supabase.from("fulfilments").select("id, sales_order_id").eq("org_id", orgId).in("sales_order_id", foundIds),
  ]);
  if (itemsRes.error) throw new Error(itemsRes.error.message);

  const items = (itemsRes.data ?? []).map((row) => ({
    ...row,
    products: firstRelation(row.products),
    variants: firstRelation(row.variants),
  }));
  const itemIds = items.map((it) => it.id as string);
  const fulfilmentIds = (fulfilRes.data ?? []).map((f) => f.id as string);

  const ledgerRefs = [...foundIds, ...itemIds];
  const [ledgerRes, shipRes] = await Promise.all([
    ledgerRefs.length
      ? supabase
          .from("inventory_ledger")
          .select("entry_type, qty_change, location_id, reference_type, reference_id, variant_id, catalogue_variant_identity_id, locations(name)")
          .eq("org_id", orgId)
          .in("entry_type", ["allocation", "deallocation"])
          .in("reference_id", ledgerRefs)
      : Promise.resolve({ data: [] as never[], error: null }),
    fulfilmentIds.length
      ? supabase
          .from("shipments")
          .select("fulfilment_id, label_url, tracking_number, service_name, shipping_provider, carrier_reference")
          .eq("org_id", orgId)
          .in("fulfilment_id", fulfilmentIds)
      : Promise.resolve({ data: [] as never[], error: null }),
  ]);
  if (ledgerRes.error) throw new Error(ledgerRes.error.message);
  if (shipRes.error) throw new Error(shipRes.error.message);

  const identity = await resolvePrintIdentity(parseOrgBusinessIdentity(orgRes.data?.name, orgRes.data?.settings));
  const externalByOrder = new Map((externalRes.data ?? []).map((e) => [e.entity_id as string, e.external_id as string]));
  const fulfilByOrder = new Map<string, string[]>();
  for (const f of fulfilRes.data ?? []) {
    const list = fulfilByOrder.get(f.sales_order_id as string) ?? [];
    list.push(f.id as string);
    fulfilByOrder.set(f.sales_order_id as string, list);
  }
  const ships = shipRes.data ?? [];
  const methodByOrder = new Map<string, string>();
  for (const [orderId, fids] of fulfilByOrder) {
    const ship = ships.find((s) => fids.includes(s.fulfilment_id as string) && ((s.service_name || s.shipping_provider) as string | null));
    const method = (ship?.service_name || ship?.shipping_provider || "").trim();
    if (method) methodByOrder.set(orderId, method);
  }

  const ledger: AllocationLedgerEntry[] = (ledgerRes.data ?? []).map((row) => ({
    entry_type: row.entry_type,
    qty_change: row.qty_change,
    location_id: row.location_id,
    reference_type: row.reference_type,
    reference_id: row.reference_id,
    variant_id: historicalVariantId(row),
    location_name: firstRelation(row.locations)?.name ?? null,
  }));

  const imageByLine = await loadLineImageUrls(orgId, items);

  const orderIndex = new Map(orders.map((o, i) => [o.id as string, i]));
  const models = orders
    .map((o) => {
      const lineRows = items.filter((it) => it.sales_order_id === o.id && !it.is_bundle);
      return buildOrderDocumentModel({
        orgId,
        orderId: o.id as string,
        orderNumber: o.order_number as string,
        orderDate: o.order_date as string | null,
        source: o.source as string,
        connectorProvider: o.connectors?.provider ?? null,
        connectorName: o.connectors?.name ?? null,
        externalOrderId: externalByOrder.get(o.id as string) ?? null,
        customerName: o.customers?.name ?? null,
        customerPhone: o.customers?.phone ?? null,
        fulfilmentLocation: o.locations?.name ?? null,
        shippingAddress: (o.shipping_address as Record<string, unknown> | null) ?? null,
        billingAddress: (o.billing_address as Record<string, unknown> | null) ?? null,
        paymentStatus: o.payment_status as string | null,
        notes: o.notes as string | null,
        customerReference: o.customer_reference as string | null,
        shippingMethod: methodByOrder.get(o.id as string) ?? null,
        money: {
          subtotal: o.subtotal as number | null,
          tax: o.tax as number | null,
          shipping: o.shipping as number | null,
          discount: o.discount as number | null,
          total: Number(o.total ?? 0),
          currency: o.currency as string | null,
        },
        lines: lineRows.map((it) => ({
          id: it.id as string,
          salesOrderId: it.sales_order_id as string,
          productId: (it.product_id as string | null) ?? null,
          // Read-only attribution for reservation history, never a live write target.
          variantId: historicalVariantId(it),
          productName: historicalProductName(it),
          optionValues: (it.variant_attributes as Record<string, unknown> | null) ?? it.variants?.option_values ?? null,
          sku: it.sku as string | null,
          variantSku: it.sku || hasDetachedCatalogueIdentity(it) ? null : it.variants?.sku ?? null,
          productSku: it.sku || hasDetachedCatalogueIdentity(it) ? null : it.products?.sku ?? null,
          barcode: hasDetachedCatalogueIdentity(it) ? null : it.variants?.barcode ?? it.products?.barcode ?? null,
          qtyOrdered: Number(it.qty_ordered ?? 0),
          qtyAllocated: Number(it.qty_allocated ?? 0),
          qtyFulfilled: Number(it.qty_fulfilled ?? 0),
          unitPrice: Number(it.unit_price ?? 0),
          lineTotal: Number(it.total ?? 0),
          imageUrl: imageByLine.get(it.id as string) ?? null,
        })),
        ledger,
        identity,
      });
    })
    .sort((a, b) => (orderIndex.get(a.orderId) ?? 0) - (orderIndex.get(b.orderId) ?? 0));

  return assertTenantIsolation(models, orgId);
}

type MediaLinkRow = {
  variant_id?: string;
  product_id?: string;
  is_primary: boolean | null;
  position: number | null;
  media_assets?: { storage_key?: string | null; status?: string | null } | { storage_key?: string | null; status?: string | null }[] | null;
};

function primaryStorageKey(rows: MediaLinkRow[], idKey: "variant_id" | "product_id", id: string): string | null {
  const matched = rows
    .filter((r) => r[idKey] === id)
    .map((r) => ({
      is_primary: r.is_primary === true,
      position: Number(r.position ?? 0) || 0,
      storage_key: firstRelation(r.media_assets)?.storage_key ?? null,
      asset_status: firstRelation(r.media_assets)?.status ?? "active",
    }))
    .filter((r) => r.storage_key && r.asset_status !== "archived")
    .sort((a, b) => Number(b.is_primary) - Number(a.is_primary) || a.position - b.position);
  return matched[0]?.storage_key ?? null;
}

async function loadLineImageUrls(
  orgId: string,
  items: Array<{ id: unknown; product_id?: unknown; variant_id?: unknown }>,
): Promise<Map<string, string>> {
  const urls = new Map<string, string>();
  const variantIds = [...new Set(items.map((it) => it.variant_id as string | null).filter((id): id is string => Boolean(id)))];
  const productIds = [...new Set(items.map((it) => it.product_id as string | null).filter((id): id is string => Boolean(id)))];
  try {
    const [variantRes, productRes] = await Promise.all([
      variantIds.length
        ? supabase
            .from("variant_media")
            .select("variant_id, is_primary, position, media_assets(storage_key, status)")
            .eq("org_id", orgId)
            .eq("status", "active")
            .in("variant_id", variantIds)
        : Promise.resolve({ data: [] as MediaLinkRow[], error: null }),
      productIds.length
        ? supabase
            .from("product_media")
            .select("product_id, is_primary, position, media_assets(storage_key, status)")
            .eq("org_id", orgId)
            .eq("status", "active")
            .in("product_id", productIds)
        : Promise.resolve({ data: [] as MediaLinkRow[], error: null }),
    ]);
    if (variantRes.error || productRes.error) return urls;
    const variantRows = (variantRes.data ?? []) as MediaLinkRow[];
    const productRows = (productRes.data ?? []) as MediaLinkRow[];
    const choices = items.map((it) => {
      const chosen = chooseCanonicalLineImage(
        it.variant_id ? primaryStorageKey(variantRows, "variant_id", it.variant_id as string) : null,
        it.product_id ? primaryStorageKey(productRows, "product_id", it.product_id as string) : null,
      );
      return { lineId: it.id as string, storageKey: chosen.storageKey };
    });
    const keys = choices.map((c) => c.storageKey).filter((k): k is string => Boolean(k));
    const signed = keys.length ? await signMediaUrls(keys) : {};
    for (const c of choices) {
      const src = printableMediaSrc(c.storageKey, signed);
      if (src) urls.set(c.lineId, src);
    }
  } catch {
    return urls;
  }
  return urls;
}

export async function printSalesOrderDocuments(
  orgId: string,
  orderIds: string[],
  kind: OrderDocumentKind,
): Promise<void> {
  const docs = await loadOrderDocumentModels(orgId, orderIds);
  if (docs.length === 0) throw new Error("No authorised orders to print.");
  const titles: Record<OrderDocumentKind, string> = {
    pick: "Pick slips",
    pack: "Packing slips",
    invoice: "Tax invoices",
  };
  const html = renderOrderDocumentsHtml(docs, kind, titles[kind]);
  openPrintWindow(html, titles[kind]);
}

export async function loadShippingLabelState(orgId: string, salesOrderId: string) {
  const fulfil = await supabase.from("fulfilments").select("id").eq("org_id", orgId).eq("sales_order_id", salesOrderId);
  if (fulfil.error) throw new Error(fulfil.error.message);
  const ids = (fulfil.data ?? []).map((f) => f.id);
  if (ids.length === 0) return shippingLabelPrintState([]);
  const ships = await supabase
    .from("shipments")
    .select("label_url, tracking_number, carrier_reference")
    .eq("org_id", orgId)
    .in("fulfilment_id", ids);
  if (ships.error) throw new Error(ships.error.message);
  return shippingLabelPrintState(ships.data ?? []);
}
